import { memo, useCallback, useEffect, useRef } from 'react';

import { useHistory, useLocation } from 'react-router';
import { AnimatePresence } from 'framer-motion';

import { Modal } from '@components';
import { useAuth, useGlobalEventChannels } from '@hooks';
import GamificationToast from '@components/GamificationToast';

import SpeakerModal from './SpeakerModal';
import ScheduleModal from './ScheduleModal';
import LogoutEventModal from './LogoutEventModal';
import CompleteProfileAvatarModal from './CompleteProfileAvatarModal';
import GamificationRulesModal from './GamificationRulesModal';

const GlobalModals = () => {
  const history = useHistory();
  const { user, signOut } = useAuth();
  const { search, ...location } = useLocation();

  const {
    event: globalEvent,
    gamificationNotif,
    clearGamificationNotif,
  } = useGlobalEventChannels();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(10000);
  const startTimeRef = useRef(0);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(clearGamificationNotif, remainingRef.current);
  }, [clearGamificationNotif]);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      remainingRef.current -= Date.now() - startTimeRef.current;
    }
  }, []);

  const resumeTimer = useCallback(() => {
    if (remainingRef.current > 0) {
      startTimer();
    }
  }, [startTimer]);

  useEffect(() => {
    if (!gamificationNotif) return undefined;
    remainingRef.current = 10000;
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [gamificationNotif, startTimer]);

  const queryParams = new URLSearchParams(search);

  const speakerId = queryParams.get('speaker');
  const scheduleSessionId = queryParams.get('session');
  const gamificationRules = queryParams.get('gamification_rules');

  const handleCloseModal =
    (key: 'speaker' | 'session' | 'gamification_rules') => () => {
      queryParams.delete(key);

      history.replace({
        ...location,
        search: `?${queryParams.toString()}`,
      });
    };

  return (
    <>
      <AnimatePresence>
        {user.avatar === 'undefined' && (
          <Modal hideCloseButton>
            <CompleteProfileAvatarModal />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {speakerId && (
          <Modal noPadding handleClose={handleCloseModal('speaker')}>
            <SpeakerModal speakerId={+speakerId} />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {scheduleSessionId && (
          <Modal noPadding handleClose={handleCloseModal('session')}>
            <ScheduleModal sessionId={+scheduleSessionId} />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {gamificationRules && (
          <Modal noPadding handleClose={handleCloseModal('gamification_rules')}>
            <GamificationRulesModal />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {globalEvent.type && (
          <Modal handleClose={signOut}>
            <LogoutEventModal
              type={globalEvent.type}
              message={globalEvent.message}
            />
          </Modal>
        )}
      </AnimatePresence>

      {gamificationNotif && (
        <GamificationToast
          heading={gamificationNotif.heading}
          footnotes={gamificationNotif.footnotes}
          visible
          onClose={clearGamificationNotif}
          onPause={pauseTimer}
          onResume={resumeTimer}
        />
      )}
    </>
  );
};

export default memo(GlobalModals);
